import { TimesheetWorkedTimeBlockFragment } from 'generated/resources/graphql'
import _ from 'lodash'

import { BreakConfiguration } from 'modules/attendance/hooks/useBreakConfigurationsByIds'
import { AggregateWorkedTimeBlocksFilter } from 'modules/attendance/store/models/AggregateWorkedTimeFilter'

interface Params {
  workedTimeBlocks: TimesheetWorkedTimeBlockFragment[]
  workedMinutes: number
  breakConfigurationsByIds: Record<number, BreakConfiguration>
  extraHourMinutes: number
}

export interface Contribution {
  name: string
  minutes: number
}

export class BreakdownWorkedTime {
  workedTimeBlocks: TimesheetWorkedTimeBlockFragment[]
  workedMinutes: number
  breakConfigurationsByIds: Record<number, BreakConfiguration>
  extraHourMinutes: number

  constructor({
    workedTimeBlocks,
    workedMinutes,
    breakConfigurationsByIds,
    extraHourMinutes,
  }: Params) {
    this.workedTimeBlocks = workedTimeBlocks
    this.workedMinutes = workedMinutes
    this.breakConfigurationsByIds = breakConfigurationsByIds
    this.extraHourMinutes = extraHourMinutes
  }

  get filteredWorkedTimeBlocks() {
    return new AggregateWorkedTimeBlocksFilter(this.workedTimeBlocks)
  }

  get breaksBlocks() {
    return this.filteredWorkedTimeBlocks.reset.breaks.filteredBlocks
  }

  get breaksContributions(): Contribution[] {
    return _(this.breaksBlocks)
      .filter(
        (block) =>
          !!block.timeSettingsBreakConfigurationId &&
          block.timeSettingsBreakConfigurationId in this.breakConfigurationsByIds
      )
      .groupBy((block) => block.timeSettingsBreakConfigurationId)
      .values()
      .map((breaksBlocks) => {
        // _.first cannot be nul because groupBy creates a hash of non-empty arrays.
        // .timeSettingsBreakConfigurationId cannot be null because we previosuly filtered out by it.
        // breakConfigurationsByIds contains the id because we are previously filtering by it.
        const breakConfigurationId = _.first(breaksBlocks)!.timeSettingsBreakConfigurationId! // eslint-disable-line no-type-assertion/no-type-assertion
        const breakConfiguration = this.breakConfigurationsByIds[breakConfigurationId]! // eslint-disable-line no-type-assertion/no-type-assertion

        return {
          name: breakConfiguration.name,
          minutes: _.sumBy(breaksBlocks, (block) => block.minutes),
        }
      })
      .value()
  }

  get breaksMinutes() {
    return _.sumBy(this.breaksBlocks, (block) => block.minutes)
  }

  get specialHoursBlocks() {
    return this.filteredWorkedTimeBlocks.reset.withTimeSettingsCategory.approved.filteredBlocks
  }

  get specialHoursContributions(): Contribution[] {
    return _(this.specialHoursBlocks)
      .reject((block) => _.isNil(block.timeRangeCategoryId) || _.isNil(block.timeRangeCategoryName))
      .groupBy((block) => block.timeRangeCategoryId)
      .values()
      .map((blocks) => {
        //breaksBlocks is not empty because groupBy creates a hash of non-empty arrays.
        //.timeRangeCategoryName is not nil because we are filtering first in the previous .reject.
        return {
          name: _.first(blocks)!.timeRangeCategoryName!, // eslint-disable-line no-type-assertion/no-type-assertion
          minutes: _.sumBy(blocks, (block) => block.minutes),
        }
      })
      .value()
  }

  get regularMinues() {
    return this.workedMinutes - this.extraHourMinutes - this.breaksMinutes
  }
}
